import { FC, memo, useState } from 'react';
import s from './ChangePassword.module.scss';
import { Button } from 'components/common/Button';
import { useToggle } from 'lib/hooks/useToggle';
import { Input } from 'components/common/Input';
import { ReactComponent as OpenEye } from 'images/openEye.svg';
import { validatePasswordField } from 'lib/utils/common/validatePasswordField';
import { useChangePasswordMutation } from 'store/features/user/user-api';
import { PortalComponent } from 'components/common/PortalComponent';
import { Loader } from 'components/common/Loader/Loader';
import { useQueryError } from 'lib/hooks/useQueryError';
import { Modal } from 'components/common/Modal/Modal';
import { useOutletContext } from 'react-router-dom';
import { ModalNavigationContext } from 'components/pages/ConsultantPage/ConsultantPage';
import { useTranslation } from 'react-i18next';
import { ErrorModal } from 'components/common/ErrorModal/ErrorModal';

const icons = {
  openEye: <OpenEye />,
};

type PasswordFieldProps = {
  title: string;
  value: string;
  setValue: React.Dispatch<React.SetStateAction<string>>;
  placeholder?: string;
};

type ChangePasswordProps = {
  // finishPasswordChange: () => void;
};

const PasswordField: FC<PasswordFieldProps> = memo(
  ({ title, value, setValue, placeholder }) => {
    const [isVisible, setIsVisible] = useToggle(false);

    const handleChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
      setValue(e.target.value);
    };

    return (
      <fieldset className={s['change-password__field']}>
        <legend>{title}</legend>

        <div className={s['change-password__input-wrap']}>
          <Input
            type={isVisible ? 'text' : 'password'}
            name={title}
            value={value}
            onChange={handleChange}
            placeholder={placeholder || title}
            required
          />

          <Button
            type='button'
            data-visible={isVisible && 'visible'}
            onClick={() => setIsVisible(!isVisible)}
          >
            {icons.openEye}
          </Button>
        </div>
      </fieldset>
    );
  },
);
PasswordField.displayName = 'PasswordField';

export const ChangePassword: FC<ChangePasswordProps> = () => {
  const { t } = useTranslation();

  const { closeRouteModal, goBack } =
    useOutletContext<ModalNavigationContext>();

  const [changePassword, { isLoading, isError, error: queryError }] =
    useChangePasswordMutation();
  const { showError, errorMessage, closeError } = useQueryError(
    isError,
    queryError,
  );

  const [currentPassword, setCurrentPassword] = useState<string>('');
  const [newPassword, setNewPassword] = useState<string>('');
  const [confirmPassword, setConfirmPassword] = useState<string>('');
  const [error, setError] = useState<string>('');

  const isDisabled = !validateChangePasswordForm({
    currentPassword,
    newPassword,
    confirmPassword,
    t,
  }).isValid;

  const handleChangePassword: React.FormEventHandler<HTMLFormElement> = async (
    e,
  ) => {
    e.preventDefault();
    setError('');

    if (isLoading) return;

    const validForm = validateChangePasswordForm({
      currentPassword,
      newPassword,
      confirmPassword,
      t,
    });
    if (!validForm.isValid) {
      setError(validForm.error);
      return;
    }

    const result = await changePassword({
      oldPassword: currentPassword,
      newPassword,
    });

    if ('error' in result) return;

    goBack();
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
  };

  // if (isSuccess)
  //   return (
  //     <Result
  //       message={data?.message || 'Пароль успешно изменен'}
  //       // backFn={finishPasswordChange}
  //       backFn={() => {}}
  //     />
  //   );

  // if (showError) return <Result message={errorMessage} backFn={closeError} />;

  return (
    <Modal
      title={t('profile.account.changePassword')}
      onClose={closeRouteModal}
      onBack={goBack}
    >
      <form className={s['change-password']} onSubmit={handleChangePassword}>
        <PasswordField
          title={t('profile.account.currentPassword')}
          value={currentPassword}
          setValue={setCurrentPassword}
          placeholder='********'
        />
        <PasswordField
          title={t('profile.account.newPassword')}
          value={newPassword}
          setValue={setNewPassword}
          placeholder='********'
        />
        <PasswordField
          title={t('profile.account.repeatNewPassword')}
          value={confirmPassword}
          setValue={setConfirmPassword}
          placeholder='********'
        />

        <span
          className={s['change-password__error']}
          role='alert'
          aria-live='polite'
        >
          {error}
        </span>

        <Button
          type='submit'
          className={s['change-password__submit']}
          isAccent={true}
          isRounded={true}
          disabled={isDisabled}
        >
          {t('common.apply')}
        </Button>

        {isLoading && (
          <PortalComponent>
            <Loader />
          </PortalComponent>
        )}

        {showError && (
          <PortalComponent>
            <ErrorModal message={errorMessage} onClose={closeError} />
          </PortalComponent>
        )}
      </form>
    </Modal>
  );
};

// const Result = (props: { message: string; backFn: () => void }) => {
//   return (
//     <div className={s['change-password__result']}>
//       {props.message}
//       <Button
//         type='button'
//         className={s['change-password__back']}
//         onClick={props.backFn}
//       >
//         Назад
//       </Button>
//     </div>
//   );
// };

function validateChangePasswordForm({
  currentPassword,
  newPassword,
  confirmPassword,
  t,
}: {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
  t: (key: string) => string;
}) {
  const currentValidation = validatePasswordField(currentPassword);
  if (!currentValidation.isValid) return currentValidation;

  const newValidation = validatePasswordField(newPassword);
  if (!newValidation.isValid) return newValidation;

  const confirmValidation = validatePasswordField(confirmPassword);
  if (!confirmValidation.isValid) return confirmValidation;

  if (newPassword !== confirmPassword)
    return {
      isValid: false,
      error: t('profile.account.errors.passwordsDoNotMatch'),
    };

  if (currentPassword === newPassword)
    return {
      isValid: false,
      error: t('profile.account.errors.currentAndNewPasswordAreTheSame'),
    };

  return { isValid: true, error: '' };
}
